import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    SmsStatusDTO,
    useDeleteSmsStatusApi,
    useSaveSmsStatusApi,
    useSearchSmsStatusApi,
} from '../../../api/sms-status-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveSmsStatusModal } from './save-sms-status-modal';

export function SmsStatusApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedSmsStatus, setSelectedSmsStatus] = useState<SmsStatusDTO | undefined>();
    const smsStatusInit = useSearchSmsStatusApi();
    const saveSmsStatus = useSaveSmsStatusApi();
    const deleteSmsStatus = useDeleteSmsStatusApi();

    const { result, error, busy, initialized, initializing } = smsStatusInit;

    const columns: IBasicTableColumn<SmsStatusDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 80,
            },
            {
                Header: <T>title</T>,
                name: 'title',
                width: 80,
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        smsStatusInit.post(null, {});
    }, [initialized, initializing, error, smsStatusInit]);

    const handleDelete = (smsStatus: SmsStatusDTO) => {
        bd.msgbox(
            translate('delete-sms-status'),
            translate('confirm-delete-sms-status'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteSmsStatus.remove(
                            smsStatus,
                            () => {
                                smsStatusInit.updateResult({
                                    ...result,
                                    smsStatuses: result.smsStatuses.filter(
                                        b => b.id !== smsStatus.id
                                    ),
                                });
                                notify.dark(translate('selected-sms-status-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedSmsStatus(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (smsStatus: SmsStatusDTO) => {
        saveSmsStatus.post(
            null,
            smsStatus,
            res => {
                if (selectedSmsStatus) {
                    smsStatusInit.updateResult({
                        ...result,
                        smsStatuses: result.smsStatuses.map(b =>
                            b.id === smsStatus.id ? res.smsStatus : b
                        ),
                    });
                } else {
                    smsStatusInit.updateResult({
                        ...result,
                        smsStatuses: [res.smsStatus, ...result.smsStatuses],
                    });
                }

                setShowModal(false);
                setSelectedSmsStatus(undefined);
                notify.success(
                    selectedSmsStatus
                        ? translate('selected-sms-status-updated-successfully')
                        : translate('new-sms-status-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => smsStatusInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="sms-status"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-sms-status</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedSmsStatus(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.smsStatuses}
                        onRowClick={({ row }) => {
                            setSelectedSmsStatus(row);
                            setShowModal(true);
                        }}
                        hover
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    <SaveSmsStatusModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedSmsStatus(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedSmsStatus={selectedSmsStatus}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
